import './reservation-timer.scss';

import { Affix, Modal } from 'antd';
import Paragraph from 'antd/lib/typography/Paragraph';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import React, { useEffect, useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';

import { IApplicationState } from '../../store';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import { actionCreators as GiftVoucherActions, GiftVoucherStatus } from '../../store/GiftVoucher';
import { actionCreators as PaymentActions } from '../../store/Payments';
import { actionCreators as UIActions } from '../../store/UI';
import usePrevious from '../../hooks/usePrevious';
import Heading, { HeadingSize } from '../heading/heading';
import ZLButton, { ButtonType } from '../zl-button/zl-button';
import { useLocation } from 'react-router';
import useQueryString from '../../hooks/useQueryString';
import { PurchaseTypes } from '../../store/PurchaseTypes';
import Logging from '../../service/logging';

export interface IExpiredModalProps {
    showModal: boolean;
}

const getTimerString = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds - min * 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const ReservationTimer: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const reservationTime = Number(process.env.REACT_APP_RESERVATION_TIME) ?? 8;

    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const selectedSiteId = useSelector((state: IApplicationState) => state.sites.selectedSite?.siteId);

    const bookingId = useSelector((state: IApplicationState) => state.booking.booking?.bookingId);
    const bookingCreated = useSelector((state: IApplicationState) => state.booking.booking?.createdDateTime);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);

    const giftVoucherStatus = useSelector(
        (state: IApplicationState) => state.giftVoucher.giftVoucher?.giftVoucherStatusTypeId
    );

    const giftVoucherLoading = useSelector((state: IApplicationState) => state.giftVoucher.loading);
    const bookingLoading = useSelector((state: IApplicationState) => state.giftVoucher.loading);

    const giftVoucherPurchaseRef = useSelector(
        (state: IApplicationState) => state.giftVoucher.giftVoucher?.giftVoucherRef
    );
    const bookingPurchaseRef = useSelector((state: IApplicationState) => state.booking.booking?.bookingRef);
    const currentMoment = useSelector((state: IApplicationState) => state.intl.momentLocale);

    //payment details
    const paymentLoading = useSelector((state: IApplicationState) => state.payments.loading);

    const bookingRelatedPaymentId = useSelector((state: IApplicationState) => state.booking?.charge?.relatedPaymentId);
    const giftRelatedPaymentId = useSelector((state: IApplicationState) => state.giftVoucher?.charge?.relatedPaymentId);
    const chargeRelatedPaymentId =
        selectedPurchaseType === PurchaseTypes.Booking || selectedPurchaseType === PurchaseTypes.PrivateEvent
            ? bookingRelatedPaymentId
            : giftRelatedPaymentId;

    const loading =
        selectedPurchaseType === PurchaseTypes.Booking || selectedPurchaseType === PurchaseTypes.PrivateEvent
            ? bookingLoading
            : giftVoucherLoading;
    const purchaseRef =
        selectedPurchaseType === PurchaseTypes.Booking || selectedPurchaseType === PurchaseTypes.PrivateEvent
            ? bookingPurchaseRef
            : giftVoucherPurchaseRef;

    const paused = useSelector((state: IApplicationState) => state.payments.loading);
    const siteUrlKey = useSelector((state: IApplicationState) => state.sites.selectedSite?.urlKey);

    const deadline = useMemo(
        () => moment(bookingCreated).add(reservationTime, 'minutes'),
        [bookingCreated, reservationTime]
    );
    const [seconds, setSeconds] = useState(deadline.diff(moment(), 'seconds'));
    const [checkBookingCounter, setCheckCounter] = useState(2);
    const [dialogShown, setDialogShown] = useState(false);
    const [showBookingExpired, setShowBookingExpired] = useState(false);

    const previousDeadline = usePrevious({ deadline })?.deadline;
    const page = location.pathname.split('/').filter((o) => o !== '')[1];
    const processingPage = page === 'processing';
    const [processingStartTime, setProcessingStartTime] = useState<number | null>(null);
    const queryString = useQueryString();

    let timer: any = null;

    //detect when we first land on the processing page, so we can check how long the user has been waiting there
    useEffect(() => {
        if (processingPage && processingStartTime === null) {
            // Record the start time when processingPage first becomes true
            setProcessingStartTime(Date.now());
        } else if (!processingPage) {
            // Reset the start time when processingPage becomes false
            setProcessingStartTime(null);
        }
    }, [processingPage]);

    function updateSecondsIfDeadlineHasChanged() {
        if (previousDeadline && !previousDeadline.isSame(deadline)) {
            setSeconds(deadline.diff(moment(), 'seconds'));
        }
    }

    // Checking the status of a booking
    useEffect(() => {
        updateSecondsIfDeadlineHasChanged();

        //see the other use effect for gift voucher stuff
        if (selectedPurchaseType === PurchaseTypes.GiftVoucher) {
            return;
        }

        //if the booking is active, confirm things and move on
        if (bookingStatus === BookingStatus.Active && (page === 'checkout' || processingPage)) {
            dispatch(BookingActions.confirmBookingSuccess());
            setShowBookingExpired(false);

            return;
        }

        // Determine if we should be checking the status or if the booking is/should be expired
        var shouldRunStatusCheck = false;
        if (processingPage) {
            // if we are on the processing page, run the status check for a few minutes to see if payment succeeds in background
            // after a few minutes assume the booking is expired / dead on the api/db side
            // and let the user know we have charged them without giving them anything for it
            //eg stripe with a 3Ds auth step that completes after the booking expires on the background

            if (processingStartTime) {
                const currentTime = Date.now();
                const timeToWait = 2 * 60 * 1000; // minutes (in milliseconds)
                const timeSpent = currentTime - processingStartTime;
                if (timeSpent >= timeToWait) {
                    //log error message to our logging api
                    let logMsg = `Error processing payment. User lingered on processing page for over ${timeToWait} milliseconds. BookingId: ${bookingId}. PaymentId: ${chargeRelatedPaymentId}`;

                    let newError = new Error(logMsg);
                    newError.name = 'Processing Payment Error';
                    Logging.LogError(newError);

                    //display translated error message to user letting them know they need to contact the venue
                    let purchaseType = t('purchaseTypes.bookingSingular');
                    let paymentType = t('payments.titles.payment');
                    let msg = t('errors.payments.contactVenue', {
                        purchaseType: purchaseType,
                        purchaseId: bookingId,
                        paymentGateway: paymentType,
                        paymentId: chargeRelatedPaymentId,
                    });
                    dispatch(UIActions.displayError('errors.payments.error', msg));

                    //booking experience now in unrecoverable state; refresh the page and try again buddy
                } else {
                    //console.log(`we are on processing page ${timeSpent} >= ${timeToWait}`);
                    shouldRunStatusCheck = true;
                }
            } else {
                shouldRunStatusCheck = true;
            }
        } else if (bookingStatus === BookingStatus.Pending && seconds > 0) {
            //if the booking is pending and we have seconds remaining
            shouldRunStatusCheck = true;
        } else if (bookingStatus === BookingStatus.Pending && !loading) {
            //if the booking is pending but we have run out of time, and are not waiting for some processing, expire the booking
            setShowBookingExpired(true);
        } else if (bookingStatus === BookingStatus.Expired) {
            //otherwise if the booking is expired
            setShowBookingExpired(true);
        }

        //if we have a reason to be checking the status of the booking, start the timer
        if (shouldRunStatusCheck) {
            //clear any old version of the timer
            clearInterval(timer);

            //set up a new one
            timer = setInterval(() => {
                // Update seconds and perform checks
                const newSeconds = deadline.diff(moment(), 'seconds');
                setSeconds(newSeconds);

                // Check booking status periodically
                if (checkBookingCounter === 0 && purchaseRef) {
                    dispatch(BookingActions.checkBookingExpired(purchaseRef));

                    //check if the stripe payment was successful
                    const paymentIntent = queryString.get('payment_intent');
                    if (paymentIntent) {
                        dispatch(PaymentActions.stripePaymentStatus(paymentIntent));
                    }

                    setCheckCounter(4); //run the api check every x seconds
                } else {
                    setCheckCounter(checkBookingCounter - 1);
                }
            }, 1000);
        }

        return () => {
            clearInterval(timer);
        };
    }, [
        seconds,
        checkBookingCounter,
        purchaseRef,
        bookingCreated,
        dispatch,
        bookingStatus,
        loading,
        deadline,
        previousDeadline,
        paused,
        location.pathname,
        processingPage,
        processingStartTime,
    ]);

    // Checking the status of the gift voucher
    useEffect(() => {
        if (selectedPurchaseType !== PurchaseTypes.GiftVoucher) {
            return;
        }

        if (giftVoucherStatus === GiftVoucherStatus.Pending) {
            timer = setInterval(() => {
                if (checkBookingCounter === 0 && purchaseRef && selectedSiteId) {
                    dispatch(GiftVoucherActions.getGiftVoucherDetails(selectedSiteId, purchaseRef));
                    const paymentIntent = queryString.get('payment_intent');
                    if (paymentIntent && processingPage) {
                        dispatch(PaymentActions.stripePaymentStatus(paymentIntent));
                    }
                    setCheckCounter(4);
                } else {
                    setCheckCounter(checkBookingCounter - 1);
                }
            }, 1000);
        } else if (giftVoucherStatus === GiftVoucherStatus.Active) {
            if (page === 'checkout' || processingPage) {
                dispatch(GiftVoucherActions.confirmGiftVoucherSuccess());
            }
        }

        return () => {
            clearInterval(timer);
        };
    }, [checkBookingCounter, purchaseRef, dispatch, giftVoucherStatus]);

    // Showing expiration modal if booking expired
    useEffect(() => {
        if (showBookingExpired && !dialogShown && !paymentLoading) {
            setDialogShown(true);

            if (purchaseRef && bookingId) {
                dispatch(BookingActions.bookingExpired(purchaseRef, bookingId));
            }

            clearInterval(timer);

            const modal = Modal.info({
                icon: undefined,
                centered: true,
                width: 'auto',
                maskClosable: false,
                className: 'expired-modal',
                title: <Heading level={HeadingSize.One}>{t('timer.titles.sessionExpired')}</Heading>,
                content: (
                    <Paragraph>
                        <Trans i18nKey="timer.labels.startOver">
                            To start over, return to the
                            <ZLButton
                                type={ButtonType.Link}
                                onClick={() => {
                                    dispatch(BookingActions.expireBooking());
                                    dispatch(BookingActions.clearBooking());
                                    if (siteUrlKey) {
                                        dispatch(push(`/${currentMoment}/book-now/${siteUrlKey}`));
                                    } else {
                                        dispatch(push(`/${currentMoment}/`));
                                    }
                                    modal.destroy();
                                }}
                            >
                                Select Tickets Page
                            </ZLButton>
                        </Trans>
                    </Paragraph>
                ),
                okButtonProps: { style: { display: 'none' } },
            });
        }
    }, [showBookingExpired, dialogShown, purchaseRef, bookingId, loading, paymentLoading]);

    const minutes = Math.ceil(seconds / 60);
    const translation = t('timer.labels.hurryUp', { value: getTimerString(seconds) });
    const visualState =
        bookingStatus !== BookingStatus.Pending || processingPage
            ? 'hidden'
            : minutes > 1
            ? 'warning'
            : minutes === 1
            ? 'danger'
            : '';

    return (
        <React.Fragment>
            <Affix offsetTop={0}>
                <div className={`reservation-timer ${visualState}`}>
                    <Text>{translation}</Text>
                </div>
            </Affix>
        </React.Fragment>
    );
};

export default ReservationTimer;
